<script setup>
import { replaceUnderscoreWithSpace } from './helper/filterHelper.js';

import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

defineProps({
  appliedFilters: { type: Array, default: () => [] },
  maxVisibleFilters: { type: Number, default: 2 },
  clearButtonLabel: { type: String, default: '' },
  moreFiltersLabel: { type: String, default: '' },
  showClearButton: { type: Boolean, default: true },
});

const emit = defineEmits(['clearFilters', 'openFilter']);

const { t } = useI18n();

const shouldCapitalizeFirstLetter = key => {
  const lowercaseKeys = ['email'];
  return !lowercaseKeys.includes(key);
};

const formatOperatorLabel = operator => {
  const operators = {
    equal_to: t('CONTACTS_FILTER.OPERATOR_LABELS.equal_to'),
    not_equal_to: t('CONTACTS_FILTER.OPERATOR_LABELS.not_equal_to'),
    contains: t('CONTACTS_FILTER.OPERATOR_LABELS.contains'),
    does_not_contain: t('CONTACTS_FILTER.OPERATOR_LABELS.does_not_contain'),
    is_present: t('CONTACTS_FILTER.OPERATOR_LABELS.is_present'),
    is_not_present: t('CONTACTS_FILTER.OPERATOR_LABELS.is_not_present'),
    is_greater_than: t('CONTACTS_FILTER.OPERATOR_LABELS.is_greater_than'),
    is_less_than: t('CONTACTS_FILTER.OPERATOR_LABELS.is_less_than'),
    days_before: t('CONTACTS_FILTER.OPERATOR_LABELS.days_before'),
  };

  return operators[operator] || replaceUnderscoreWithSpace(operator);
};

const formatOperatorKey = key => {
  const keys = {
    name: t('CONTACTS_FILTER.ATTRIBUTES.NAME'),
    email: t('CONTACTS_FILTER.ATTRIBUTES.EMAIL'),
    phone_number: t('CONTACTS_FILTER.ATTRIBUTES.PHONE_NUMBER'),
    identifier: t('CONTACTS_FILTER.ATTRIBUTES.IDENTIFIER'),
    city: t('CONTACTS_FILTER.ATTRIBUTES.CITY'),
    country: t('CONTACTS_FILTER.ATTRIBUTES.COUNTRY'),
    custom_attribute_list: t(
      'CONTACTS_FILTER.ATTRIBUTES.CUSTOM_ATTRIBUTE_LIST'
    ),
    custom_attribute_text: t(
      'CONTACTS_FILTER.ATTRIBUTES.CUSTOM_ATTRIBUTE_TEXT'
    ),
    custom_attribute_number: t(
      'CONTACTS_FILTER.ATTRIBUTES.CUSTOM_ATTRIBUTE_NUMBER'
    ),
    custom_attribute_link: t(
      'CONTACTS_FILTER.ATTRIBUTES.CUSTOM_ATTRIBUTE_LINK'
    ),
    custom_attribute_checkbox: t(
      'CONTACTS_FILTER.ATTRIBUTES.CUSTOM_ATTRIBUTE_CHECKBOX'
    ),
    created_at: t('CONTACTS_FILTER.ATTRIBUTES.CREATED_AT'),
    last_activity: t('CONTACTS_FILTER.ATTRIBUTES.LAST_ACTIVITY'),
    referer_link: t('CONTACTS_FILTER.ATTRIBUTES.REFERER_LINK'),
    blocked: t('CONTACTS_FILTER.ATTRIBUTES.BLOCKED'),
  };
  return keys[key.toString().toLowerCase()] || replaceUnderscoreWithSpace(key);
};

const formatFilterQueryOperator = operator => {
  const operators = {
    AND: t('CONTACTS_FILTER.QUERY_DROPDOWN_LABELS.AND'),
    OR: t('CONTACTS_FILTER.QUERY_DROPDOWN_LABELS.OR'),
  };

  return operators[operator.toString().toUpperCase()] || operator;
};

const formatFilterValue = value => {
  // Case 1: null, undefined, empty string
  if (!value) return '';

  // Case 2: array → map each item, use name if present, else the item itself
  if (Array.isArray(value)) {
    return value.map(item => item?.name ?? item).join(', ');
  }

  // Case 3: object with a "name" property → return name
  // Case 4: primitive (string, number, etc.) → return as is
  return value?.name ?? value;
};
</script>

<template>
  <div class="flex flex-wrap items-center w-full gap-2 mx-auto">
    <template v-for="(filter, index) in appliedFilters" :key="index">
      <div
        v-if="index < maxVisibleFilters"
        class="inline-flex items-center gap-2 h-7"
      >
        <div
          class="flex items-center h-full min-w-0 gap-1 px-2 py-1 text-xs border rounded-lg hover:bg-n-solid-2 max-w-72 border-n-weak hover:cursor-pointer"
          @click="emit('openFilter')"
        >
          <span
            class="lowercase whitespace-nowrap first-letter:capitalize text-n-slate-12"
          >
            {{ formatOperatorKey(filter.attributeKey) }}
          </span>
          <span class="px-1 text-xs text-n-slate-10 whitespace-nowrap">
            {{ formatOperatorLabel(filter.filterOperator) }}
          </span>
          <span
            v-if="filter.values"
            :title="formatFilterValue(filter.values)"
            class="lowercase truncate text-n-slate-12"
            :class="{
              'first-letter:capitalize': shouldCapitalizeFirstLetter(
                filter.attributeKey
              ),
            }"
          >
            {{ formatFilterValue(filter.values) }}
          </span>
        </div>
        <template
          v-if="
            index < maxVisibleFilters - 1 && index < appliedFilters.length - 1
          "
        >
          <span
            class="content-center h-full px-1 text-xs font-medium uppercase rounded-lg text-n-slate-10"
          >
            {{ formatFilterQueryOperator(filter.queryOperator) }}
          </span>
        </template>
      </div>
    </template>
    <div
      v-if="appliedFilters.length > maxVisibleFilters"
      class="inline-flex items-center content-center px-1 text-xs rounded-lg text-n-slate-10 hover:text-n-slate-11 h-7 hover:cursor-pointer"
      @click="emit('openFilter')"
    >
      {{ moreFiltersLabel }}
    </div>
    <div v-if="showClearButton" class="w-px h-3 rounded-lg bg-n-strong" />
    <Button
      v-if="showClearButton"
      :label="clearButtonLabel"
      size="xs"
      class="!px-1"
      variant="ghost"
      @click="emit('clearFilters')"
    />
  </div>
</template>
